import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services/user.service';

function CommissionPopup({ text, closeCommissionPopup }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [commissionList, setCommissionList] = useState([]);
    const [monthList, setMonthList] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [paidClaims, setPaidClaims] = useState('');
    const [commissionPerClaim, setCommissionPerClaim] = useState('');
    const [isRuleBased, setIsRuleBased] = useState('');
    const [totalBonusClaims, setTotalBonusClaims] = useState('');
    const [chainCommissionList, setChainCommissionList] = useState([]);
    const [isOverride, setIsOverride] = useState(false);
    const [isBonusAllowed, setIsBonusAllowed] = useState(false);

    const userdata = useMemo(() => {
        try {
            const user = localStorage.getItem('user');
            if (!user || user === '' || user === '{}') {
                userService.logout();
                return null;
            }
            return JSON.parse(user);
        } catch {
            userService.logout();
            return null;
        }
    }, []);

    const fetchCommissionData = useCallback(async (monthName = '') => {
        if (!userdata) return;

        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/ws/wh_commission_type', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    whname: userdata.paramsname || userdata.wholesaler,
                    wholesaler_Id: userdata.wholesaler_Id,
                    mname: monthName
                })
            });

            const apidata = await response.json();

            if (apidata?.length > 0) {
                let commPerClaim;
                if (apidata[0].commList_Commission[0].isRuleBased === "1.00") {
                    commPerClaim = JSON.parse(apidata[0].commList_Commission[0].commission);
                } else {
                    commPerClaim = apidata[0].commList_Commission[0].commission;
                }

                setCommissionList(apidata[0].commList);
                setIsRuleBased(apidata[0].commList_Commission[0].isRuleBased);
                setCommissionPerClaim(commPerClaim);
                setPaidClaims(apidata[0].commList_Commission[0].paidClaims);
                setMonthList(apidata[0].monthList);
                setIsOverride(apidata[0].isOverride);
                setTotalBonusClaims(apidata[0].commList_Commission[0].totalBonusClaims);
                setIsBonusAllowed(apidata[0].isBonusAllowed);
                
                if (!selectedMonth && apidata[0].monthList?.length > 0) {
                    setSelectedMonth(apidata[0].monthList[0].monthYearname);
                }
            } else {
                //setError('Data not found');
            }
        } catch (err) {

            //setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [userdata, selectedMonth]);

    const fetchChainCommission = useCallback(async (monthName = '') => {
        if (!userdata) return;

        try {
            const data = await userService.wh_ChainCommission(
                userdata.paramsname || userdata.wholesaler,
                userdata.wholesaler_Id,
                monthName
            );
            
            if (data?.length > 0) {
                setChainCommissionList(data);
            }
        } catch (err) {

        }
    }, [userdata]);

    useEffect(() => {
        fetchCommissionData('');
        fetchChainCommission('');
    }, []);

    const handleMonthChange = useCallback((value) => {
        setSelectedMonth(value);
        fetchCommissionData(value);
        fetchChainCommission(value);
    }, [fetchCommissionData, fetchChainCommission]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeCommissionPopup();
        }
    }, [closeCommissionPopup]);

    // Calculate chain-wise values
    const { chainwisePaidClaims, chainwiseCommission, nonChainPaidClaims, nonChainCommission } = useMemo(() => {
        let chainPaid = 0;
        let chainComm = 0;
        let nonChainPaid = 0;
        let nonChainComm = 0;

        try {
            if (chainCommissionList.length > 0) {
                chainPaid = chainCommissionList.reduce((sum, item) => sum + item.paidClaims, 0);
                nonChainPaid = Number(paidClaims.replace(',', '')) - chainPaid;
                nonChainComm = Number(commissionPerClaim) * Number(nonChainPaid);
                chainComm = chainCommissionList.reduce((sum, item) => 
                    sum + (Number(item.paidClaims) * Number(item.commissionRate)), 0);
            }
        } catch {
            // ignore calculation errors
        }

        return {
            chainwisePaidClaims: chainPaid,
            chainwiseCommission: chainComm,
            nonChainPaidClaims: nonChainPaid,
            nonChainCommission: nonChainComm
        };
    }, [chainCommissionList, paidClaims, commissionPerClaim]);

    const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);
    const formatCurrency = (num) => new Intl.NumberFormat('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    }).format(num);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : commissionList.length > 0 ? (
                        <>
                            {/* Month Selector */}
                            <div className="space-y-2">
                                <Label htmlFor="month-select">Select Month, Year</Label>
                                <Select value={selectedMonth} onValueChange={handleMonthChange}>
                                    <SelectTrigger id="month-select">
                                        <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {monthList.map((item, index) => (
                                            <SelectItem key={index} value={item.monthYearname}>
                                                {item.monthYearname.replace(',', ', ')}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Commission Details Table */}
                            <div className="border border-border rounded-lg overflow-hidden">
                                <Table>
                                    <TableBody>
                                    {/* General Claims & Commission */}
                                    {chainCommissionList.length === 0 && (
                                        <>
                                            <TableRow>
                                                <TableCell className="font-medium w-1/2 border-r border-dashed px-2 py-3">
                                                    Total Paid Claims{chainCommissionList.length > 0 ? ' (General)' : ''}:
                                                </TableCell>
                                                <TableCell>
                                                    {formatNumber(Number(paidClaims.replace(',', '')) - chainwisePaidClaims)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    Commission per Claim ($){chainCommissionList.length > 0 ? ' (General)' : ''}:
                                                </TableCell>
                                                <TableCell>
                                                    {isRuleBased === "1.00" ? (
                                                        <div className="text-left">
                                                            {Array.isArray(commissionPerClaim) && commissionPerClaim.map((item, index) => (
                                                                <div key={index} className="text-sm">
                                                                    {item.from} - {item.to}: ${item.commission}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span>${commissionPerClaim}</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                    {/* Chain Commission */}
                                    {chainCommissionList.map((obj, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    Total Paid Claims ({obj.pharmacy_Chain_Name}):
                                                </TableCell>
                                                <TableCell>
                                                    {formatNumber(obj.paidClaims)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    Commission per Claim ($) ({obj.pharmacy_Chain_Name}):
                                                </TableCell>
                                                <TableCell>
                                                    ${obj.commissionRate.toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}

                                    {/* Bonus Claims */}
                                    {isBonusAllowed && (
                                        <>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Total Bonus Claims</TableCell>
                                                <TableCell>{totalBonusClaims}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    {commissionList[10]?.wholesalerCommissionType}
                                                </TableCell>
                                                <TableCell>
                                                    ${commissionList[10]?.wholesalerCommissionTotal}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                    {/* Total Revenue */}
                                    <TableRow className="bg-muted/30">
                                        <TableCell className="font-bold border-r border-dashed px-2 py-3">
                                            {commissionList[0]?.wholesalerCommissionType}
                                        </TableCell>
                                        <TableCell className="font-bold px-2 py-3">
                                            ${chainwiseCommission > 0 
                                                ? formatCurrency(chainwiseCommission + nonChainCommission)
                                                : commissionList[0]?.wholesalerCommissionTotal
                                            }
                                        </TableCell>
                                    </TableRow>

                                    {/* Override Commission */}
                                    {isOverride && (
                                        <>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    {commissionList[1]?.wholesalerCommissionType}
                                                </TableCell>
                                                <TableCell>
                                                    ${commissionList[1]?.wholesalerCommissionTotal}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                    {commissionList[2]?.wholesalerCommissionType}
                                                </TableCell>
                                                <TableCell>
                                                    ${commissionList[2]?.wholesalerCommissionTotal}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                    {/* Bonus Commission */}
                                    {isBonusAllowed && (
                                        <TableRow>
                                            <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                                {commissionList[11]?.wholesalerCommissionType}
                                            </TableCell>
                                            <TableCell>
                                                ${commissionList[11]?.wholesalerCommissionTotal}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {/* Payable amounts */}
                                    <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                        <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">
                                            {commissionList[3]?.wholesalerCommissionType}
                                        </TableCell>
                                        <TableCell className="font-medium px-2 py-3 text-white">
                                            ${commissionList[3]?.wholesalerCommissionTotal}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                        <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">
                                            {commissionList[4]?.wholesalerCommissionType}
                                        </TableCell>
                                        <TableCell className="font-medium px-2 py-3 text-white">
                                            ${commissionList[4]?.wholesalerCommissionTotal}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                        <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">
                                            {commissionList[5]?.wholesalerCommissionType}
                                        </TableCell>
                                        <TableCell className="font-medium px-2 py-3 text-white">
                                            ${commissionList[5]?.wholesalerCommissionTotal}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                            </div>
                        </>
                    ) : (
                        <div className="border border-border rounded-lg overflow-hidden">
                            <Table>
                                <TableBody>
                                <TableRow>
                                    <TableCell className="font-medium border-r border-dashed px-2 py-3">Total Paid Claims</TableCell>
                                    <TableCell>0</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="font-medium border-r border-dashed px-2 py-3">Total Revenue</TableCell>
                                    <TableCell>$0.00</TableCell>
                                </TableRow>
                                <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                    <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">Total Payable</TableCell>
                                    <TableCell className="font-medium px-2 py-3 text-white">$0.00</TableCell>
                                </TableRow>
                                <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                    <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">Payable To Distributor</TableCell>
                                    <TableCell className="font-medium px-2 py-3 text-white">$0.00</TableCell>
                                </TableRow>
                                <TableRow className="bg-primary hover:bg-primary border-b border-sidebar-accent">
                                    <TableCell className="font-medium border-r border-dashed border-sidebar-accent px-2 py-3 text-white">Payable To Sub Distributor</TableCell>
                                    <TableCell className="font-medium px-2 py-3 text-white">$0.00</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-destructive mt-2">{error}</p>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={closeCommissionPopup} className="w-auto">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CommissionPopup;
